<template>
    <ResourceCard
        v-if="event.data"
        class="event-card"
        document-type="event"
        :to="redirectLink"
        :title="event.data.title"
        :excerpt="event.data.excerpt"
        :image="event.data.thumbnail"
        :is-upcoming-event="isUpcomingEvent"
        :base-data-testid="baseDataTestid"
        :resource-categories="(props.event.data?.resourceCategories as {resourceCategory: ResourceCategoryDocument}[]) ?? undefined"
        :category="event.data.category"
    >
        <template #image-overlay>
            <div :data-testid="`${baseDataTestid}--thumbnail`">
                <EventImageOverlay :date="event.data.date" :is-upcoming-event="isUpcomingEvent" />
            </div>
        </template>
    </ResourceCard>
</template>

<script lang="ts" setup>
    import {computed, type ComputedRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import type {FilledContentRelationshipField} from '@prismicio/types';
    import type {EventDocument, EventDocumentData, ResourceCategoryDocument} from '@prismicio-types';
    import {useLocalePrefix} from '#imports';
    import linkResolver from '~/app/prismic/link-resolver';
    import EventImageOverlay from '~/components/EventImageOverlay.vue';
    import ResourceCard from '~/components/ResourceCard.vue';
    import {createDateHelpers} from '~/composables/dateHelpers';

    const {withLocalePrefix} = useLocalePrefix();

    const props = defineProps<{
        event: EventDocument | FilledContentRelationshipField<'event', string, EventDocumentData>;
    }>();

    const baseDataTestid = computed(() => props.event.uid ?? props.event.id);
    const shouldUseNewCard = computed(() => {
        if (!props.event?.data) return false;
        const sl = props.event.data.signupLink;
        if (!isFilled.link(sl)) return true;
        if (sl.link_type === 'Web') return !sl.url;
        return !('url' in sl && sl.url);
    });
    const {isBefore, isSameDay} = createDateHelpers();
    const isUpcomingEvent = computed(
        (): boolean =>
            isBefore(new Date(), new Date(props.event.data?.date as string)) || isSameDay(new Date(), new Date(props.event.data?.date as string)),
    );

    const titleLink = computed(() => (isUpcomingEvent.value ? props.event.data?.signupLink : props.event.data?.replayLink));
    const oldCardLink = computed(() => {
        if (isUpcomingEvent.value && isFilled.link(props.event.data?.signupLink)) return props.event.data.signupLink.url;
        if (isFilled.link(props.event.data?.replayLink)) return props.event.data.replayLink.url;
        if (isFilled.link(titleLink.value)) return titleLink.value.url;
        return '/resources/events';
    }) as ComputedRef<string>;
    const redirectLink = computed(() => {
        const link = shouldUseNewCard.value ? linkResolver(props.event) : oldCardLink.value;
        return link.startsWith('/') ? withLocalePrefix(link) : link;
    });
</script>

<style lang="scss">
    .event-card {
        width: auto;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        position: relative;
    }
</style>
